import { CatalogProvider } from '@common/catalog/CatalogProvider';
import { doNotRetryIfEntityNotFound, doNotShowErrorIfEntityNotFound } from '@common/Entity/utils';
import { EntityNotFound } from '@common/EntityNotFound';
import { EntityToCreate } from '@common/EntityToCreate/EntityToCreate';
import { LinkGit } from '@common/ExternalGitStatus';
import { useGitStatus } from '@common/ExternalGitStatus/useExternalGitStatus';
import { SnowflakePwdAuthBanner } from '@common/Fabric/SnowflakePwdAuthBanner';
import { IDELogContextProvider } from '@common/Footer/context';
import { CodeGenerationContextProvider } from '@common/git/CodeGeneration/context';
import { useBranchListQuery } from '@common/git/git-hooks';
import { ProjectContextProvider } from '@common/git/Project/context';
import { IDELayoutWithFooter } from '@common/ide/Layout';
import { IdeActionProvider, IdeStateProvider, useIDE, useIDEParams } from '@common/ide/state';
import { IdeState } from '@common/ide/types';
import { isPipelineOnboardingEnabled, OnboardingProvider, useSparkOnboardingValue } from '@common/onboarding/hooks';
import { useProjectBrowserOpenPreference } from '@common/preferences/userPreference';
import { ProjectPreferenceProvider, useProjectPreferenceStore } from '@common/project/projectPreference';
import { QueryKeys } from '@common/queries/common';
import { ReactDndManager } from '@common/react-dnd-manager';
import { CommonReplaySessionProvider } from '@common/Replay/utils';
import { Entity, IDEEntity } from '@common/types/Entity';
import { getJobIdeUrl } from '@common/url';
import { BackEndCodeLanguage, BackEndCodeLanguageType, WorkflowModeType } from '@prophecy/graphql-types/enums';
import { Centered, Spinner, Stack, StackItem } from '@prophecy/ui-v3';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { noop } from 'lodash-es';
import queryString from 'query-string';
import { useEffect, useMemo, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { Navigate, useLocation } from 'react-router-dom';

import { ExecutionServiceProvider } from '../../context/execution';
import { MetricsProjectContext } from '../../context/metrics/MetricsProjectContext';
import { useMixPanel } from '../../context/mixpanel/context';
import { MixPanelContextKeys } from '../../context/mixpanel/types';
import { useExecutionServiceInstance } from '../../data/executionHooks';
import { useGraphQlQuery } from '../../data/util';
import { LAST_JOB_IDE_URL, LAST_VISITED_IDE_URL } from '../../utils/localstorage-keys';
import { GemIDE } from '../gem/ide/Playground';
import { Job } from '../Job';
import { ObservationContainer } from '../Observation/ObservationContainer';
import { ObservationHome } from '../Observation/ObservationHome';
import ProjectLineage from '../ProjectLineage/ProjectLineage';
import { ProjectBrowserIDE } from '../Workflow/IDEProjectBrowser';
import { Pipeline } from '../Workflow/Workflow';
import {
  ConfigurationProjectExternalSyncQuery,
  GemFromOtherProjectQuery,
  GemProjectExternalSyncQuery,
  JobProjectExternalSyncQuery,
  ObservationProjectExternalSyncQuery,
  PipelineFromOtherProjectQuery,
  PipelineProjectExternalSyncQuery,
  ProjectExternalSyncQuery
} from './query';

function useProjectExternalSyncQuery({
  uid,
  entity,
  projectId,
  isUIDFromAnotherProject
}: {
  uid: string;
  entity: IDEEntity;
  projectId?: string;
  isUIDFromAnotherProject: boolean;
}) {
  const projectExternalSyncVariables = { uid: uid };
  const entityFromOtherProjectQueryVariable = { uid: uid, ownerProjectUID: '' };
  const projectVariables = { uid: projectId as string };
  const { observedEntity } = useIDEParams();
  // if projectId is there, assumes user is trying to open other project entities(job/pipeline)
  if (isUIDFromAnotherProject && projectId) {
    entityFromOtherProjectQueryVariable.ownerProjectUID = projectId;
  }
  const { data: projectData, isInitialLoading: isProjectLoading } = useGraphQlQuery(
    [QueryKeys.Project, projectVariables],
    ProjectExternalSyncQuery,
    { enabled: Boolean(projectId) },
    projectVariables
  );

  const { data: pipelineData, isInitialLoading: isPipelineLoading } = useGraphQlQuery(
    [QueryKeys.Project, QueryKeys.Pipeline, projectExternalSyncVariables],
    PipelineProjectExternalSyncQuery,
    {
      enabled: entity === Entity.Pipeline && !isUIDFromAnotherProject,
      retry: doNotRetryIfEntityNotFound,
      onError: doNotShowErrorIfEntityNotFound
    },
    projectExternalSyncVariables
  );
  const { data: pipelineData1, isInitialLoading: isPipelineLoading1 } = useGraphQlQuery(
    [QueryKeys.Project, QueryKeys.Pipeline, entityFromOtherProjectQueryVariable],
    PipelineFromOtherProjectQuery,
    { enabled: entity === Entity.Pipeline && isUIDFromAnotherProject },
    entityFromOtherProjectQueryVariable
  );
  const { data: pipelineConfigurationsData, isInitialLoading: isPipelineConfigurationsLoading } = useGraphQlQuery(
    [QueryKeys.Project, QueryKeys.PipelineConfiguration, projectExternalSyncVariables],
    ConfigurationProjectExternalSyncQuery,
    { enabled: entity === Entity.Configuration },
    projectExternalSyncVariables
  );
  const { data: jobData, isInitialLoading: isJobLoading } = useGraphQlQuery(
    [QueryKeys.Project, QueryKeys.Job, projectExternalSyncVariables],
    JobProjectExternalSyncQuery,
    { enabled: entity === Entity.Job, retry: doNotRetryIfEntityNotFound, onError: doNotShowErrorIfEntityNotFound },
    projectExternalSyncVariables
  );
  const { data: gemData, isInitialLoading: isGemLoading } = useGraphQlQuery(
    [QueryKeys.Project, QueryKeys.Gem, projectExternalSyncVariables],
    GemProjectExternalSyncQuery,
    // dont show error for gem query
    { enabled: entity === Entity.Gem && !isUIDFromAnotherProject, onError: noop, retry: false },
    projectExternalSyncVariables
  );
  const { data: gemData1, isInitialLoading: isGemLoading1 } = useGraphQlQuery(
    [QueryKeys.Project, QueryKeys.Gem, entityFromOtherProjectQueryVariable],
    GemFromOtherProjectQuery,
    // dont show error for gem query
    { enabled: entity === Entity.Gem && isUIDFromAnotherProject, onError: noop, retry: false },
    entityFromOtherProjectQueryVariable
  );

  const { data: observationData, isInitialLoading: isObservationLoading } = useGraphQlQuery(
    [QueryKeys.Project, QueryKeys.Expectation, projectExternalSyncVariables],
    ObservationProjectExternalSyncQuery,
    {
      enabled: entity === Entity.Observation && observedEntity !== Entity.Connector,
      retry: false,
      refetchInterval: 1000000
    },
    projectExternalSyncVariables
  );
  const isLoading =
    isPipelineLoading ||
    isPipelineLoading1 ||
    isPipelineConfigurationsLoading ||
    isJobLoading ||
    isGemLoading ||
    isGemLoading1 ||
    isProjectLoading ||
    isObservationLoading;

  const project = projectId
    ? projectData?.Project
    : pipelineData?.Pipeline?.project ||
      pipelineConfigurationsData?.Configuration?.project ||
      jobData?.Job?.project ||
      gemData?.Gem?.project ||
      observationData?.Project;
  const repositoryPath =
    entity === Entity.Configuration
      ? pipelineConfigurationsData?.Configuration?.basePipeline.repositoryPath
      : isUIDFromAnotherProject
        ? gemData1?.Gem?.repositoryPath || pipelineData1?.Pipeline?.repositoryPath
        : undefined;
  const path = pipelineData?.Pipeline?.path || pipelineConfigurationsData?.Configuration?.basePipeline.repositoryPath;
  const workflowMode = (pipelineData?.Pipeline?.mode ||
    pipelineConfigurationsData?.Configuration?.basePipeline?.mode) as WorkflowModeType | undefined;
  const projectRef = useRef(project);
  /**
   * persist project object when navigating b/w entities from project browser to avoid reloading of IDE
   */
  useEffect(() => {
    if (project) {
      projectRef.current = project;
    }
  }, [project]);
  const obj = useMemo(() => {
    return {
      // eslint-disable-next-line react-compiler/react-compiler
      isLoading: projectRef.current ? false : isLoading,
      data: {
        repositoryPath,
        path,
        // eslint-disable-next-line react-compiler/react-compiler
        project: project || projectRef.current,
        workflowMode
      }
    };
  }, [repositoryPath, isLoading, project, path, workflowMode]);

  return obj;
}

function EntityRenderer({
  branch,
  entity,
  ideKey,
  isUIDFromAnotherProject,
  projectId,
  state
}: {
  branch: string;
  entity: IDEEntity;
  ideKey: string;
  isUIDFromAnotherProject?: boolean;
  projectId: string;
  state: IdeState;
}) {
  switch (entity) {
    case Entity.Observation:
      return <ObservationContainer />;
    case Entity.Gem:
      return (
        <GemIDE
          isAirflow={false}
          key={ideKey}
          branch={branch}
          uidOrRepositoryPath={state.ide.repositoryPath ? state.ide.repositoryPath : state.ide.uid}
          currentProjectId={state.project.id}
          custom={false}
        />
      );
    case Entity.Pipeline:
      return (
        <Pipeline
          key={ideKey}
          isUIDFromAnotherProject={isUIDFromAnotherProject}
          uid={state.ide.repositoryPath ? state.ide.repositoryPath : state.ide.uid}
          projectId={state.project.id}
          version={state.ide.version}
          branch={state.ide.branch}
        />
      );

    case Entity.Configuration:
      return state.ide.repositoryPath ? (
        <Pipeline
          key={ideKey}
          isUIDFromAnotherProject={isUIDFromAnotherProject}
          uid={state.ide.repositoryPath ? state.ide.repositoryPath : state.ide.uid}
          projectId={state.project.id}
          version={state.ide.version}
          branch={state.ide.branch}
          configurationInstanceId={state.ide.uid}
        />
      ) : null;
    case Entity.Job:
      return <Job key={state.ide.uid} uid={state.ide.uid} branch={state.ide.branch} />;
    case Entity.Lineage:
      return <ProjectLineage projectId={projectId} />;
    default:
      return null;
  }
}

function IDE({
  entity,
  uid,
  projectId,
  projectName,
  language,
  repositoryPath,
  path,
  isUIDFromAnotherProject,
  branch,
  workflowMode
}: {
  entity: IDEEntity;
  uid: string;
  projectId: string;
  isUIDFromAnotherProject?: boolean;
  projectName: string;
  language: BackEndCodeLanguageType;
  repositoryPath?: string;
  path?: string;
  branch?: string;
  workflowMode?: WorkflowModeType;
}) {
  const onboardingValue = useSparkOnboardingValue(uid);
  const pipelineOnboardingEnabled = isPipelineOnboardingEnabled(onboardingValue);

  const [persistedPBOpen, setPersistedPBOpen] = useProjectBrowserOpenPreference();
  const onPersistProjectBrowser = usePersistentCallback((show: boolean) => {
    setPersistedPBOpen(show);
  });
  const { state, actions, setActions } = useIDE({
    uid,
    entity,
    projectId,
    projectName,
    language,
    branch,
    repositoryPath,
    path,
    defaultShowProjectBrowser: pipelineOnboardingEnabled ? false : Boolean(persistedPBOpen),
    onPersistProjectBrowser
  });

  const ideKey = `${state.ide.repositoryPath} ${state.ide.uid}`;
  const executionService = useExecutionServiceInstance(ideKey);
  const { addContext, removeContext } = useMixPanel();
  const { data: ProjectBranchData } = useBranchListQuery({
    projectId: projectId
  });
  useLastVisitedEntity(entity, pipelineOnboardingEnabled);
  const workingBranch = ProjectBranchData?.Project.listBranches.workingBranch || branch || '';
  useEffect(() => {
    addContext(MixPanelContextKeys.Project, {
      branch: workingBranch,
      id: projectId,
      name: projectName
    });
    return () => {
      removeContext(MixPanelContextKeys.Project);
    };
  }, [workingBranch, addContext, removeContext, projectId, projectName]);

  const codeGenProps = useMemo(
    () => ({
      isPipelineIDE: entity === Entity.Pipeline || entity === Entity.Configuration
    }),
    [entity]
  );

  return (
    <ExecutionServiceProvider value={executionService}>
      <DndProvider manager={ReactDndManager}>
        <IdeStateProvider value={state}>
          <IdeActionProvider value={{ setActions, actions }}>
            <CatalogProvider>
              <Stack height='100%'>
                <SnowflakePwdAuthBanner />
                <StackItem grow='1'>
                  <IDELayoutWithFooter>
                    <IDELogContextProvider entity={entity}>
                      <CodeGenerationContextProvider {...codeGenProps}>
                        <EntityRenderer
                          branch={state.ide.branch ?? ''}
                          entity={entity}
                          isUIDFromAnotherProject={isUIDFromAnotherProject}
                          projectId={projectId}
                          state={state}
                          ideKey={ideKey}
                        />

                        <EntityToCreate />
                        <ProjectBrowserIDE workflowMode={workflowMode} />
                      </CodeGenerationContextProvider>
                    </IDELogContextProvider>
                  </IDELayoutWithFooter>
                </StackItem>
              </Stack>
            </CatalogProvider>
          </IdeActionProvider>
        </IdeStateProvider>
      </DndProvider>
    </ExecutionServiceProvider>
  );
}

export default function IDEPage() {
  const { uid, entity, branch, projectId, isUIDFromAnotherProject, observedEntity } = useIDEParams();
  const onboardingValue = useSparkOnboardingValue(uid);

  const resp = useProjectExternalSyncQuery({
    uid,
    entity,
    projectId: projectId,
    isUIDFromAnotherProject
  });

  const {
    data: { project, repositoryPath, workflowMode, path },
    isLoading
  } = resp;
  const {
    projectPreferences,
    isLoading: isProjectPreferenceLoading,
    setProjectPreference
  } = useProjectPreferenceStore(project?._id ?? '', true);
  const { shouldLinkGitToProceed, isLoading: isExternalGitStatusLoading } = useGitStatus(project?._id ?? '');
  if (observedEntity === Entity.Connector) return <ObservationHome />;
  if (isLoading || isExternalGitStatusLoading || isProjectPreferenceLoading) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }
  // if project fetch is completed and there is no project then redirect to home page
  if (!project) {
    return <EntityNotFound uid={uid} entity={entity} projectContext={true} />;
  }

  if (project.language === BackEndCodeLanguage.sql && entity === Entity.Job) {
    return <Navigate to={getJobIdeUrl(uid, project._id, BackEndCodeLanguage.sql, branch)} />;
  }

  return shouldLinkGitToProceed ? (
    <LinkGit projectId={project._id} />
  ) : (
    <ProjectPreferenceProvider preferences={projectPreferences} setPreference={setProjectPreference}>
      <CommonReplaySessionProvider>
        <ProjectContextProvider projectId={project._id}>
          <MetricsProjectContext />
          <OnboardingProvider value={onboardingValue}>
            <IDE
              key={project._id}
              branch={branch}
              uid={uid}
              path={path as string}
              repositoryPath={repositoryPath as string}
              entity={entity}
              isUIDFromAnotherProject={isUIDFromAnotherProject}
              projectId={project._id}
              projectName={project.name}
              language={project.language}
              workflowMode={workflowMode}
            />
          </OnboardingProvider>
        </ProjectContextProvider>
      </CommonReplaySessionProvider>
    </ProjectPreferenceProvider>
  );
}

function useLastVisitedEntity(entity: Entity, ignore: boolean) {
  const location = useLocation();
  // omit run-id for pipeline or onboarding
  const { 'run-id': _pipelineRunId, onboarding: _onboarding, ...queryParams } = queryString.parse(location.search);
  const uri = queryString.stringifyUrl({ url: location.pathname, query: queryParams });
  useEffect(() => {
    if (ignore) {
      return;
    }
    if (entity === Entity.Job) {
      localStorage.setItem(LAST_JOB_IDE_URL, uri);
    } else if (entity === Entity.Configuration || entity === Entity.Pipeline || entity === Entity.Lineage) {
      localStorage.setItem(LAST_VISITED_IDE_URL, uri);
    }
  }, [entity, uri, ignore]);
}
